import { SUGGESTED_TAB } from 'context/reducers/tabs.type';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IGetTruckerTypeReturn, fetchPage, fetchTruckerList } from 'services/trucker';
import { transformDataToTracking } from 'utils/transformDataToTracking';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useTracking } from 'hooks/useTracking';
import { useAppContext } from 'hooks/useAppContext';
import { useGlobals } from 'hooks/useGlobals';
import { usePayPerUseExtraTruckers } from 'hooks/usePayPerUseExtraTruckers';
import { useNewRecommendationBff } from 'hooks/useNewRecommendationBff';
import { fetchTruckersRecommendedList, fetchTruckersRecommendedPage } from 'services/truckerRecommended';
import { useQueryClient } from '@tanstack/react-query';

export const useLoadTruckers = (type: 'onload' | 'lazy' = 'onload') => {
  const {
    state: {
      truckers: { radius, meta },
      tabs: { activeTab },
    },
    dispatch,
  } = useAppContext();

  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { getItemAsJson, removeItem } = useLocalStorage();
  const { user } = useGlobals();
  const { isPayPerUsePlan } = usePayPerUseExtraTruckers();
  const { isNewRecommendationBff } = useNewRecommendationBff();
  const queryClient = useQueryClient();

  const { freightId = 'j3ZlGd' } = useParams();

  const sendTrackingAmplitude = (amplitudeSourceParam: string, response: IGetTruckerTypeReturn, freightID?: string) => {
    const lockedTruckersAmount = response?.meta?.truckersLimitsTotal?.locked;
    const unlockedTruckersAmount = response?.meta?.truckersLimitsTotal?.unlocked;
    const totalTruckers = lockedTruckersAmount + unlockedTruckersAmount;

    sendAmplitude('trucker_listing_suggested_opened', {
      source: amplitudeSourceParam,
      drive: response.data,
      freightId,
      companyId: user?.data?.company?.id,
      isPayPerUsePlan,
      truckersNumbers: {
        unlocked: unlockedTruckersAmount,
        locked: lockedTruckersAmount,
        total: totalTruckers,
      },
    });
    sendTracking({
      event: {
        name: 'trucker_listing_suggested_opened',
      },
      data: {
        source: amplitudeSourceParam,
        drive: transformDataToTracking(response.data),
        freightId: `hashed|${freightID || freightId}`,
      },
    });
  };

  const dispatchSuggestedTruckers = (response: IGetTruckerTypeReturn) => {
    dispatch({ type: 'truckers_meta_set', payload: response.meta });
    dispatch({ type: 'suggested_truckers_load_success', payload: response.data });
  };

  const getValidRangeValue = (currentValue: number): number => {
    if (currentValue === 0) return 5;

    return currentValue;
  };

  // eslint-disable-next-line default-param-last
  const fetchTruckers = async (range: number, amplitudeSourceParam?: string) => {
    const updatedRange = getValidRangeValue(range);

    dispatch({ type: 'suggested_truckers_load' });
    try {
      let response;
      if (isNewRecommendationBff) {
        response = await queryClient.fetchQuery({
          queryKey: ['truckers-recommendation-list', freightId],
          queryFn: () => fetchTruckersRecommendedList(freightId, updatedRange),
          retry: false,
        });
      } else {
        response = await fetchTruckerList(freightId, updatedRange);
      }

      if (amplitudeSourceParam) {
        sendTrackingAmplitude(amplitudeSourceParam, response);
      }
      dispatchSuggestedTruckers(response);
    } catch (errorMessage) {
      dispatch({ type: 'suggested_truckers_load_error', payload: errorMessage });
    }
  };

  const fetchTruckersWithFreightId = async (freightID: string, range: number, amplitudeSourceParam?: string) => {
    const updatedRange = getValidRangeValue(range);
    dispatch({ type: 'suggested_truckers_load' });
    try {
      let response;
      if (isNewRecommendationBff) {
        response = await queryClient.fetchQuery({
          queryKey: ['truckers-recommendation-list', freightID],
          queryFn: () => fetchTruckersRecommendedList(freightID, updatedRange),
          retry: false,
        });
      } else {
        response = await fetchTruckerList(freightID, updatedRange);
      }
      if (amplitudeSourceParam) {
        sendTrackingAmplitude(amplitudeSourceParam, response, freightID);
      }

      dispatchSuggestedTruckers(response);
    } catch (errorMessage) {
      dispatch({ type: 'suggested_truckers_load_error', payload: errorMessage });
    }
  };

  const getListActiveTab = async () => {
    const itemFromContactPoint = getItemAsJson('truckerId');
    if (activeTab === SUGGESTED_TAB && itemFromContactPoint) {
      fetchTruckers(radius, 'contact-point');
      removeItem('truckerId');
      return;
    }
    if (activeTab === SUGGESTED_TAB) {
      fetchTruckers(radius, 'botao-buscar-motoristas-listagem-de-fretes');
    }
  };

  useEffect(() => {
    if (type === 'onload') {
      getListActiveTab();
    }
  }, [activeTab]);

  const fetchNextTruckerPage = async (range: number) => {
    dispatch({ type: 'suggested_truckers_page_load' });

    try {
      let response;
      if (isNewRecommendationBff) {
        response = await queryClient.fetchQuery({
          queryKey: ['truckers-recommendation-list', freightId],
          queryFn: () => fetchTruckersRecommendedPage(freightId, getValidRangeValue(range), meta?.pagination?.next),
          retry: false,
        });
      } else {
        response = await fetchPage(freightId, getValidRangeValue(range), meta?.pagination?.next);
      }

      dispatch({
        type: 'suggested_truckers_page_load_success',
        payload: response.data,
      });

      dispatch({ type: 'truckers_meta_set', payload: response.meta });
    } catch (errorMessage) {
      dispatch({ type: 'suggested_truckers_load_error', payload: errorMessage });
    }
  };
  return { fetchTruckers, fetchNextTruckerPage, fetchTruckersWithFreightId };
};
